import { BMDivider, BMPrimitives, BMText } from "@traveloka/district-bloom";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { StyleSheet } from "react-native";
import BMView from "../../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../../core/resource/MobileResourceProvider";
import { BMDrawer } from "../../BMDrawer";
import DisableTravelerPickerProvider from "../Provider/DisableTravelerPickerContext";
import TravelerPickerErrorHandler from "../TravelerPickerErrorHandler";
import { Employee } from "../types";
import SelectNonEmployeeContent from "./SelectNonEmployeeContent";
import { SelectNonEmployeeFormValues } from "./types";

type Props = {
  isVisible: boolean;
  onClose(): void;
  onSubmit(values: SelectNonEmployeeFormValues): void;
  initialNonEmployeeValue?: SelectNonEmployeeFormValues;
  employees: Employee[];
};

export default function SelectNonEmployeeDrawer(props: Props) {
  const { initialNonEmployeeValue, isVisible, onClose, onSubmit, employees } =
    props;

  const { CorporateTravelerPicker: cr } = useMobileContentResource();

  const methods = useForm<SelectNonEmployeeFormValues>({
    defaultValues: initialNonEmployeeValue,
  });
  const {
    control,
    formState: { isDirty, errors },
    handleSubmit,
  } = methods;

  const adults = useWatch({
    control,
    name: "adults",
  });

  function handleSubmitButton(values: SelectNonEmployeeFormValues) {
    // Filter age zero, prevent null pointer if childrenAges is undefined (product isn't hotel)
    const formValues = {
      ...values,
      childrenAges: values.childrenAges
        ? values.childrenAges.filter(Boolean)
        : [],
    };
    onSubmit(formValues);
    onClose();
  }

  return (
    <BMDrawer
      headerNavigationProps={{
        onBackActionPress: onClose,
        title: cr.selectNonEmployeeTitle,
      }}
      footerButtonProps={{
        onPress: handleSubmit(handleSubmitButton),
        label: cr.selectNonEmployeeSubmitButtonText,
        disabled:
          !isDirty ||
          Object.values(errors.root ?? {}).some(
            (error) =>
              typeof error === "object" &&
              error !== null &&
              "type" in error &&
              (error as { type?: string | number }).type === "400"
          ),
        testID: "nonemployee-submit",
      }}
      show={isVisible}
      variant="COMPACT"
    >
      <TravelerPickerErrorHandler
        name={["adults", "children", "infants", "childrenAges"]}
        {...methods}
      />
      <FormProvider {...methods}>
        <DisableTravelerPickerProvider
          employees={employees}
          nonEmployeeAdults={adults}
        >
          <BMView style={styles.container} spacing="lg">
            <BMText variant="uiNormalLi" ink="BMStyleTextInkSecondary">
              {cr.selectNonEmployeeSubtitle}
            </BMText>
            <BMDivider type="SUBTLE" />
            <BMText variant="uiLarge">
              {cr.selectNonEmployeeSectionTitle}
            </BMText>
            <SelectNonEmployeeContent />
          </BMView>
        </DisableTravelerPickerProvider>
      </FormProvider>
    </BMDrawer>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: BMPrimitives.BMSize.md,
    paddingTop: BMPrimitives.BMSize.lg,
  },
});
